<template>
  <div class="ppg-backdrop" @click.self="$emit('close')">
    <div class="ppg-panel" role="dialog" aria-modal="true" aria-label="Photos of planned teeth">
      <header class="ppg-header">
        <div>
          <div class="ppg-eyebrow">Your photos</div>
          <div class="ppg-title">Photos of Planned Teeth</div>
        </div>
        <button class="ppg-close" type="button" aria-label="Close" @click="$emit('close')">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <div class="ppg-body">
        <p class="ppg-empty" v-if="photos.length === 0">
          There are no photos for this treatment plan.
        </p>
        <div v-else class="ppg-grid">
          <button
            v-for="(photo, i) in photos"
            :key="photo.photoId"
            type="button"
            class="ppg-card"
            @click="$emit('open', i)"
          >
            <img
              :src="photo.url"
              :alt="altFor(photo)"
              class="ppg-thumb"
              loading="lazy"
              @error="$emit('refresh-urls')"
            />
            <span class="ppg-teeth" v-if="teethOf(photo).length > 0">
              <span v-for="t in teethOf(photo)" :key="t" class="ppg-tooth-chip">#{{ t }}</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The "Photos of Planned Teeth" pop-over — used by Present Mode and the
 * patient view, so the patient and the person presenting to them are always
 * looking at the same thing.
 *
 * Purely presentational. The caller has already narrowed `photos` to the ones
 * this plan shows (utils/photoFilter.js) and owns the lightbox; this emits
 * `open` with an index rather than opening one itself, so the two surfaces
 * keep their own lightbox mounting.
 */
export default {
  name: 'PlanPhotosGallery',
  props: {
    photos: { type: Array, default: () => [] },
  },
  methods: {
    teethOf(photo) {
      return ((photo && photo.teeth) || []).slice().sort((a, b) => a - b);
    },
    altFor(photo) {
      const teeth = this.teethOf(photo);
      if (teeth.length === 0) return 'Photo from your dental visit';
      return `Photo of ${teeth.map((t) => `tooth ${t}`).join(', ')}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.ppg-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}

.ppg-panel {
  background: #fff;
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 48px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ppg-panel * { box-sizing: border-box; }

.ppg-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e4e7ec;
}

.ppg-eyebrow {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #94a3b8;
  font-weight: 600;
}

.ppg-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 3px;
}

.ppg-close {
  border: none;
  background: none;
  color: #94a3b8;
  font-size: 16px;
  cursor: pointer;
  padding: 2px 4px;

  &:hover { color: #475569; }
}

.ppg-body {
  padding: 18px 20px 22px;
  overflow-y: auto;
}

.ppg-empty {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  text-align: center;
  padding: 24px 0;
}

.ppg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
}

.ppg-card {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    border-color: #17a2a2;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #17a2a2;
    outline-offset: 2px;
  }
}

.ppg-thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
  background: #f1f5f9;
}

.ppg-teeth {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
}

.ppg-tooth-chip {
  background: #e6f7f7;
  color: #0f7c7c;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
}
</style>
